import { useEffect } from 'react';

import clsx from 'clsx';
import { FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import Avatar from '@avatar/components/Avatar';
import TutorialLegend from '@event/components/TutorialModal';
import useRandomizedContent from '@event/hooks/useRandomizedContent';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { useTutorialStore } from '@event/store/useTutorialStore';
import { CreateEvent } from '@event/types';
import usePopupStore from '@popup/stores/usePopupStore';
import ImageSelector from '@selector/components/ImageSelector';
import useClientStore from '@utils/hooks/useClientStore';

import Title from './Title';

interface AvatarTitleSelectionProps {
  register: UseFormRegister<CreateEvent>;
  setValue: UseFormSetValue<CreateEvent>;
  formState: FormState<CreateEvent>;
  photoId: number;
  setPhotoId: (photoId: number) => void;
  isTutorialEventTitle?: boolean;
  eventImage?: string;
}

const AvatarTitleSelection = ({
  register,
  setValue,
  formState,
  photoId,
  setPhotoId,
  isTutorialEventTitle = false,
  eventImage,
}: AvatarTitleSelectionProps) => {
  const {
    popupState: { isEventImageSelectorOpen },
    setPopupState,
  } = usePopupStore();
  const { selectedEventType } = useEventTypesStore();
  const { eventTitle, eventDescription } = useRandomizedContent(
    selectedEventType,
    formState.isDirty,
  );

  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  useEffect(() => {
    setValue('description', eventDescription);
    setValue('name', eventTitle);
  }, [eventDescription, eventTitle, setValue]);

  const handleAvatarChange = (newPhotoId: number) => {
    setPhotoId(newPhotoId);
    setPopupState({ isEventImageSelectorOpen: false });
  };

  return (
    <div
      className={clsx(
        'mb-4 flex items-center',
        isTutorialEventTitle &&
          'relative z-20 rounded-[16px] bg-brand-white-100',
      )}
    >
      {isTutorialEventTitle && (
        <TutorialLegend
          textBubble="Obojí jsme vám připravili. Pokud se vám nelíbí, můžete změnit."
          textHeadingCounter="2/5"
          textHeading="Obrázek a název."
          onSubmit={() => {
            tutorialStore?.goToNextStep();
          }}
          className="bottom-[-378px] left-0"
        />
      )}
      <div className="mr-2">
        <Avatar
          imgSrc={eventImage}
          title={`Avatar ${photoId}`}
          type="showModal"
          onClick={() =>
            setPopupState({
              isEventImageSelectorOpen: !isEventImageSelectorOpen,
            })
          }
          className="ignore-click-outside"
        />
      </div>
      {isEventImageSelectorOpen && (
        <ImageSelector
          onAvatarClick={(newPhotoId) => handleAvatarChange(newPhotoId)}
          type="event"
          handleClick={() =>
            setPopupState({
              isEventImageSelectorOpen: false,
            })
          }
          photoId={photoId}
          className={clsx(
            'z-20',
            isTutorialEventTitle
              ? 'md:left-[-12px] md:top-[-12px] lg:left-[-36px] lg:top-[-36px]'
              : 'md:left-[4px] md:top-[70px] lg:left-[-4px] lg:top-[78px]',
          )}
        />
      )}
      <div className="w-full overflow-x-hidden">
        <Title
          id={'name'}
          register={register}
          setValue={setValue}
          value={eventTitle}
          placeholder="Prosím vyplňte název"
          isTutorialEventTitle={isTutorialEventTitle}
        />
      </div>
    </div>
  );
};

export default AvatarTitleSelection;
